import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import ReactTooltip from 'react-tooltip';
import { useAppSelector } from '@hooks/index';
import Table from '@components/common/table/Table';
import Paginator from '@components/common/paginator/Paginator';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { configs } from '@constants/configs/configs';
import { dateMethods, DATE_FORMATS } from '@utils/dates/dateMethods';
import { FEATURE_PLAN_UPLOAD_STATUS, COLUMN_LENGTH, PAGINATION_THRESHOLD } from '@modules/plans/constant';

const FeaturePlanList = ({showUploadView}) => {
    const intl = useIntl();
    const docList = useAppSelector(state => state.featurePlan.serffDocList);
    const [ sortDocList, setSortDocList ] = useState([]);
    const [ sortBy, setSortBy ] = useState('ASC');
    const [ columnName, setColumnName ] = useState('');
    const { getDateFormatted } = dateMethods;
    const [ page, updatePage ] = useState(0);
    const totalPages = useMemo(() => Math.ceil(sortDocList.length), [ sortDocList.length ]);
    const FEATURE__TABLE_COLUMN_IDS = {
        DOC_NAME: 'docName',
        YEAR: 'serffTrackNum',
        DATE: 'createdOn',
        STATUS: 'status',
        DOWNLOAD: 'download'
    };
    const columns = [
        {
            id: FEATURE__TABLE_COLUMN_IDS.DOC_NAME,
            isSortable: true,
            text: intl.formatMessage({ id: 'issuer.featureplan.table.header.filename' }),
            type: 'link',
            sortableHandler: () => sortByHandler(FEATURE__TABLE_COLUMN_IDS.DOC_NAME, sortBy)
        },
        {
            id: FEATURE__TABLE_COLUMN_IDS.YEAR,
            isSortable: true,
            text: intl.formatMessage({ id: 'issuer.featureplan.table.header.planyear' }),
            type: 'link',
            sortableHandler: () => sortByHandler(FEATURE__TABLE_COLUMN_IDS.YEAR, sortBy)
        },
        {
            id: FEATURE__TABLE_COLUMN_IDS.DATE,
            isSortable: true,
            text: intl.formatMessage({ id: 'issuer.featureplan.table.header.date' }),
            type: 'link',
            sortableHandler: () => sortByHandler(FEATURE__TABLE_COLUMN_IDS.DATE, sortBy)
        },
        {
            id: FEATURE__TABLE_COLUMN_IDS.STATUS,
            isSortable: true,
            text: intl.formatMessage({ id: 'issuer.featureplan.table.header.status' }),
            type: 'link',
            sortableHandler: () => sortByHandler(FEATURE__TABLE_COLUMN_IDS.STATUS, sortBy)
        },
        {
            id: FEATURE__TABLE_COLUMN_IDS.DOWNLOAD,
            text: intl.formatMessage({ id: 'issuer.featureplan.table.header.download' }),
            type: 'link',
        }
    ];

    useEffect(() => {
        setSortDocList(docList);
    }, [docList]);

    const changePage = newPage => {
        updatePage(newPage);
    };

    const paginatorData = {
        first: page === 0,
        isEmpty: !sortDocList.length,
        last: page === totalPages - 1,
        pageNumber: page,
        totalPages: Math.ceil(sortDocList.length / PAGINATION_THRESHOLD)
    };

    const processedList = useMemo(() => {
        const startIndex = page * PAGINATION_THRESHOLD;
        return sortDocList.slice(startIndex, startIndex + PAGINATION_THRESHOLD);
    }, [page, sortDocList]);

    const getSortOrder = (sortBy) => sortBy === 'ASC' ? 'DESC' : 'ASC';
    
    const sortByHandler = useCallback((name ,sortBy) => {
        const sorted = [...sortDocList].sort((a, b) => {
            let aVal = a[name];
            let bVal = b[name];
            if(a[name] === FEATURE__TABLE_COLUMN_IDS.YEAR || a[name] === FEATURE__TABLE_COLUMN_IDS.DATE) {
                return sortBy === 'ASC' ? aVal - bVal : bVal - aVal;
            }
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();
            const sortOrder = getSortOrder(sortBy);
            setSortBy(sortOrder);
            if(aVal < bVal) return sortOrder === 'ASC' ? -1 : 1;
            if(aVal > bVal) return sortOrder === 'ASC' ? 1 : -1;
            return 0;
        });
        setSortDocList(sorted);
        setColumnName(name);
    }, [sortBy, columnName, sortDocList]);

    const handleClick = (e, docId) => {
        e.preventDefault();
        const serffRequestId = window.encodeURI(docId);
        const url = configs.endpoints.FEATURE_PLAN_DOWNLOAD_TEMPLATE.processReplacements({ serffRequestId: serffRequestId });
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const truncateRow = (str: string = '', maxLength: number) => {
        return str.length > maxLength ? `${str.slice(0, maxLength)}…` : str;
    };

    const TooltipWrapper = ({ id, content, toolTipTxt = content, maxLength }) => (
        <span data-tip data-for={id}>
            {content ? truncateRow(content, maxLength) : '-'}
            {content && (
                <ReactTooltip id={id} className="tooltip-component" place="bottom" type="dark" effect="solid"  multiline>
                    {toolTipTxt}
                </ReactTooltip>
            )}
        </span>
    );
    
    const processTableList = (dataList) => {
        if(dataList && dataList.length) {
            return dataList.map((row, index) => {
                const createdDate = getDateFormatted(row.createdOn, DATE_FORMATS.DATE_MMM_DD_YYYY_HH_MM);
                const toolTipText = (row.status === FEATURE_PLAN_UPLOAD_STATUS.FAILURE && row.remarks) ? row.remarks : row.status; 
                return {
                    docName: <TooltipWrapper id={`tooltip_feature_plan_doc_name_${index}`} content={row.docName} maxLength={COLUMN_LENGTH} />,
                    serffTrackNum: <TooltipWrapper id={`tooltip_feature_plan_year_${index}`} content={row.serffTrackNum} maxLength={COLUMN_LENGTH} />,
                    createdOn: <TooltipWrapper id={`tooltip_feature_plan_created_date_${index}`} content={createdDate} maxLength={COLUMN_LENGTH} />,
                    status: <TooltipWrapper id={`tooltip_feature_plan_status_${index}`} content={row.status} toolTipTxt={toolTipText} maxLength={COLUMN_LENGTH} />,
                    download: <a data-tip data-for={`tooltip_feature_plan_download_template_${index}`} href='#' onClick={(e) => handleClick(e, row.serffRequestId)}>
                        {columns[4].text}
                        <ReactTooltip 
                            place="bottom"
                            type="dark"
                            effect="solid"
                            id={`tooltip_feature_plan_download_template_${index}`}>
                            {columns[4].text}
                        </ReactTooltip>
                    </a>  
                };
            });
        }
    };

    const processTableData = processTableList(processedList);

    const showFeaturePlanList = (!showUploadView && processTableData && processTableData.length > 0 && (
        <Table id="tblFeaturePlan" className="featureListWrapper gi-tooltip" data={processTableData} columns={columns} />
    ));
    const showEmptyListMessage = (!showUploadView && (!processTableData || (processTableData && processTableData.length === 0)) && (
        <div id="feature-plan-no-records">
            <AlertMessage
                showIcon={false}
                content={<FormattedMessage id='issuer.featureplan.table.norecords' />}
                type="info"
            /> 
        </div>));
    
    const paginator = (!showUploadView && sortDocList.length > PAGINATION_THRESHOLD) && (<Paginator
        { ...{
            changePage,
            floatCenter: true,
            ...paginatorData
        } }
    />);
    return(
        <>
            {showFeaturePlanList}
            {showEmptyListMessage}
            {paginator}
        </>);
};

export default FeaturePlanList;